import { isEqual } from 'lodash-es';
import {
  formValue,
  whenAll,
  whenAny,
  whenNot,
} from 'platform/shared/health-apps';
import omit from 'platform/utilities/data/omit';
import { FORM_KEYS } from '../../constants';
import { isLoggedIn } from '../applicationStatus';
import { roleIsSponsor } from '../certifier';

const PREFILL_KEY = 'view:profilePrefill';

/**
 * Creates an accessor for a value staged by the prefill transformer.
 *
 * @param {string} path - dot-notation path within the staged prefill data
 * @returns {Function} function that accepts formData and returns the value
 */
export const prefillValue = path => formValue(`${PREFILL_KEY}.${path}`);

const prefillToggleIsOn = formData =>
  formData[FORM_KEYS.prefillEnabled] === true;

export const canUseSponsorPrefill = whenAll(
  prefillToggleIsOn,
  isLoggedIn,
  roleIsSponsor,
);

/**
 * Removes the fields a prefill sync applied and resets its applied flag.
 *
 * @param {Object} formData - the current form data
 * @param {string} appliedKey - the sync's applied flag key
 * @param {string[]} keys - form data keys to remove
 * @returns {Object} updated form data
 */
export const clearAppliedPrefill = (formData, appliedKey, keys) => ({
  ...omit(keys, formData),
  [appliedKey]: false,
});

/**
 * Builds the page predicates and sync for staged prefill values that are
 * copied into `sponsor*` fields while the prefill page is active. Only values
 * a sync applied are cleared when the page goes inactive. With a `confirmKey`,
 * answering "No" leaves the user's edits alone and shows the entry page;
 * confirming again restores the prefill values. Without one, the values are
 * read-only and are restored whenever they drift.
 *
 * @param {Object} config
 * @param {string} config.appliedKey - flag recording that the sync applied values
 * @param {string} [config.confirmKey] - Yes/No confirmation field (`false` means edit)
 * @param {Function} config.hasPrefill - whether the staged data is complete and valid
 * @param {Object<string, Function>} config.fields - form data key to prefill accessor
 * @returns {{showPrefill: Function, showEntry: Function, sync: Function}}
 *   `sync` returns the same reference when nothing changes.
 */
export const createConfirmPrefill = ({
  appliedKey,
  confirmKey,
  hasPrefill,
  fields,
}) => {
  const fieldKeys = Object.keys(fields);
  const isEditing = formData =>
    Boolean(confirmKey) && formData[confirmKey] === false;

  const showPrefill = whenAll(canUseSponsorPrefill, hasPrefill);
  const showEntry = whenAny(whenNot(showPrefill), isEditing);

  const sync = formData => {
    const isApplied = formData[appliedKey] === true;

    if (showPrefill(formData)) {
      const values = Object.fromEntries(
        fieldKeys.map(key => [key, fields[key](formData)]),
      );
      const isSynced = fieldKeys.every(key =>
        isEqual(formData[key], values[key]),
      );
      if (isApplied && (isEditing(formData) || isSynced)) return formData;
      return { ...formData, ...values, [appliedKey]: true };
    }

    if (isApplied) {
      return clearAppliedPrefill(
        formData,
        appliedKey,
        [...fieldKeys, confirmKey].filter(Boolean),
      );
    }

    return formData;
  };

  return { showPrefill, showEntry, sync };
};
